import { saveToStore, getAllFromStore, deleteFromStore, getFromStore } from './localDb';

const storeMap = {
    'tables': 'tables',
    'menu': 'menu',
    'categories': 'categories',
    'staff': 'staff'
};

let isIntercepting = false;
let originalFetch = null;

// Initialize the monkey-patch for fetch
export const initOfflineSync = () => {
    if (window.offlineSyncInitialized || isIntercepting) return;
    window.offlineSyncInitialized = true;
    isIntercepting = true;
    originalFetch = window.fetch;

    window.fetch = async (...args) => {
        let [resource, config] = args;
        
        // Always attempt standard fetch first
        let response;
        let isNetworkError = false;
        
        try {
            response = await originalFetch(resource, config);
        } catch (error) {
            isNetworkError = true;
        }

        // Check if we hit a network failure
        if (isNetworkError) {
            // Only queue mutations (POST, PUT, DELETE) to our API
            const method = config?.method?.toUpperCase() || 'GET';
            const url = typeof resource === 'string' ? resource : resource.url;
            
            // Skip queueing for GET requests or non-API requests (e.g. analytics, external)
            if (['POST', 'PUT', 'DELETE'].includes(method) && url.includes('/api/')) {
                console.warn('[OfflineSync] Intercepted offline mutation:', method, url);
                
                // Exclude /api/orders POST since that is already handled by orders_queue!
                if (method === 'POST' && url.endsWith('/api/orders')) {
                    // Let the original caller's catch block handle it (CustomerPage.js / KitchenPage.js)
                    if (isNetworkError) throw new Error('Network Error');
                    throw new Error('Offline');
                }

                const uuid = `mutation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                
                let parsedBody = null;
                try {
                    parsedBody = config.body ? JSON.parse(config.body) : null;
                } catch (e) {
                    parsedBody = config.body;
                }

                const mutationRecord = {
                    id: uuid,
                    url,
                    method,
                    headers: config.headers || {},
                    body: parsedBody,
                    created_at: new Date().toISOString()
                };

                // Save to local IndexedDB queue
                await saveToStore('api_mutations_queue', mutationRecord);

                const optimisticData = { id: `temp_${uuid}`, ...parsedBody };

                // Optimistically update the specific local store so the UI updates immediately
                const urlObj = new URL(url.startsWith('http') ? url : `http://localhost${url}`);
                const pathname = urlObj.pathname;
                const urlParts = pathname.split('/');
                const apiIndex = urlParts.indexOf('api');
                if (apiIndex !== -1) {
                    const endpoint = urlParts[apiIndex + 1];
                    const storeName = storeMap[endpoint];
                    if (storeName) {
                        try {
                            if (method === 'POST') {
                                await saveToStore(storeName, optimisticData);
                            } else if (method === 'PUT') {
                                const idStr = urlParts[urlParts.length - 1];
                                const id = isNaN(idStr) ? idStr : parseInt(idStr, 10);
                                const existing = await getFromStore(storeName, id) || {};
                                await saveToStore(storeName, { ...existing, ...parsedBody });
                            } else if (method === 'DELETE') {
                                const idStr = urlParts[urlParts.length - 1];
                                const id = isNaN(idStr) ? idStr : parseInt(idStr, 10);
                                await deleteFromStore(storeName, id);
                            }
                        } catch (storeErr) {
                            console.error('[OfflineSync] Failed to optimistically update store', storeErr);
                        }
                    }
                }

                // Trick the caller into thinking it succeeded to optimistically update UI
                const mockResponse = {
                    success: true,
                    message: 'Saved offline. Will sync when reconnected.',
                    data: optimisticData,
                    isOfflineMock: true
                };

                return new Response(JSON.stringify(mockResponse), {
                    status: 200,
                    headers: { 'Content-Type': 'application/json' }
                });
            }
        }

        // If it wasn't a network error, return the response normally
        if (isNetworkError) {
            throw new Error('Network Error');
        }
        
        return response;
    };

    // Listen for reconnection to process the queue
    window.addEventListener('online', processOfflineMutations);
    
    // Also try processing immediately on load if we are online
    if (navigator.onLine) {
        processOfflineMutations();
    }
    
    // Heartbeat to process occasionally if we miss the 'online' event
    setInterval(() => {
        if (navigator.onLine) {
            processOfflineMutations();
        }
    }, 60000);
};

const doProcessOfflineMutations = async () => {
    try {
        const queue = await getAllFromStore('api_mutations_queue');
        if (!queue || queue.length === 0) return;

        console.log(`[OfflineSync] Found ${queue.length} offline mutations. Syncing...`);
        
        // Sort by created_at to process in order
        queue.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        for (const record of queue) {
            try {
                const fetchConfig = {
                    method: record.method,
                    headers: record.headers,
                    body: typeof record.body === 'object' ? JSON.stringify(record.body) : record.body
                };

                console.log(`[OfflineSync] Replaying ${record.method} ${record.url}`);
                // Use originalFetch to bypass the interceptor
                const res = await originalFetch(record.url, fetchConfig);
                
                if (res.ok) {
                    await deleteFromStore('api_mutations_queue', record.id);
                    
                    // Cleanup optimistic temp record
                    try {
                        const urlObj = new URL(record.url.startsWith('http') ? record.url : `http://localhost${record.url}`);
                        const pathname = urlObj.pathname;
                        const urlParts = pathname.split('/');
                        const apiIndex = urlParts.indexOf('api');
                        if (apiIndex !== -1 && record.method === 'POST') {
                            const endpoint = urlParts[apiIndex + 1];
                            const storeName = storeMap[endpoint];
                            if (storeName) {
                                await deleteFromStore(storeName, `temp_${record.id}`);
                            }
                        }
                    } catch (cleanupErr) {
                        console.error('[OfflineSync] Failed to cleanup optimistic record', cleanupErr);
                    }

                    console.log(`[OfflineSync] Successfully synced mutation ${record.id}`);
                } else {
                    console.error(`[OfflineSync] Server rejected mutation ${record.id} with status ${res.status}`);
                    // Optionally handle 4xx errors by discarding, but 5xx keep in queue
                    if (res.status >= 400 && res.status < 500) {
                        // Hard failure (e.g. invalid data, unauthorized) - remove it so we don't get stuck forever
                        await deleteFromStore('api_mutations_queue', record.id);
                    }
                }
            } catch (err) {
                console.error(`[OfflineSync] Failed to replay mutation ${record.id}, will retry later`, err);
                // Stop processing the rest of the queue if we hit a network error again to preserve order
                break;
            }
        }
        
        // Notify the app that background mutations just synced successfully
        if (queue.length > 0) {
            window.dispatchEvent(new CustomEvent('offlineMutationsSynced'));
        }
    } catch (error) {
        console.error('[OfflineSync] Error processing mutations queue', error);
    }
};

let isProcessingQueue = false;

// Process the stored offline queue
export const processOfflineMutations = async () => {
    if (!navigator.onLine || !originalFetch) return;

    // Use Web Locks API to prevent cross-tab race conditions (e.g. Admin Page and POS open at same time)
    if (navigator.locks) {
        try {
            await navigator.locks.request('redsorm_offline_sync_lock', { ifAvailable: true }, async (lock) => {
                if (!lock) {
                    console.log('[OfflineSync] Another tab is already syncing mutations. Skipping.');
                    return;
                }
                await doProcessOfflineMutations();
            });
        } catch (err) {
            console.error('[OfflineSync] Web Locks API error', err);
        }
    } else {
        // Fallback for older browsers without Web Locks API
        if (isProcessingQueue) return;
        isProcessingQueue = true;
        try {
            await doProcessOfflineMutations();
        } finally {
            isProcessingQueue = false;
        }
    }
};
